import React, { useState, useEffect, useContext } from 'react'
import { useQuery, useApolloClient } from '@apollo/client'
import sortBy from 'lodash/sortBy'
import ErrorIcon from '@material-ui/icons/Error'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import Grid from '@material-ui/core/Grid'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Checkbox from '@material-ui/core/Checkbox'
import Button from '@material-ui/core/Button'
// import Launch from '@material-ui/icons/Launch'
import Close from '@material-ui/icons/Close'
import ArrowDropDown from '@material-ui/icons/ArrowDropDown'
import ArrowDropUp from '@material-ui/icons/ArrowDropUp'
// import FormControlLabel from '@material-ui/core/FormControlLabel'
import FilterInput from '../../components/FilterInput'
import StoryCard from '../StoryCard'
import { useStyles } from './styles'
import { GET_STORIES_LIST, SIGN_FILE_LINKS } from '../../network/stories/queries'
import { ALL_USERS_FILTER, NO_USERS_FILTER, STORY_TYPE_FILTERS, ALL_STORY_TYPES_FILTER } from '../../constants/common'
import Patient from '../../assets/Patient Care.svg'
import { TAG_ICONS_MAP, VIDEO_TYPE } from '../../constants/story'
import { MENU_PROPS } from '../../constants/theme'
import { LabContext } from '../../context'
import { CardMedia } from '@material-ui/core'

const StoryReview = () => {
  const client = useApolloClient()
  const classes = useStyles()
  const { selectedLab, selectedAccount } = useContext(LabContext)
  const [waveFilterValue, setWaveFilterValue] = useState(ALL_USERS_FILTER)
  const [storyTypeFilterValue, setStoryTypeFilterValue] = useState(ALL_STORY_TYPES_FILTER)
  const [savedStoriesFilterValue, setSavedStoriesFilterValue] = useState(false)
  const [tagsFilterValue, setTagsFilterValue] = useState([])
  const [defaultTagsFilterValue, setDefaultTagsFilterValue] = useState([])
  const [isSomeFilteringApplied, setIsSomeFilteringApplied] = useState(false)
  const [isTagBankCollapsed, setTagBankCollapsed] = useState(false)
  const [areTagsListDownloaded, setAreTagsListDownloaded] = useState(false)
  const [linksPairs, setLinksPairs] = useState(null)
  const [currentLab, setCurrentLab] = useState(selectedLab)

  const { loading, data } = useQuery(GET_STORIES_LIST, {
    skip: !selectedLab,
    variables: {
      labId: selectedLab,
      accountIds: selectedAccount ? [selectedAccount] : undefined,
      waveId:
        waveFilterValue.id === ALL_USERS_FILTER.id ? undefined : waveFilterValue.id === NO_USERS_FILTER.id ? null : waveFilterValue.id,
      storyType: storyTypeFilterValue.id === ALL_STORY_TYPES_FILTER.id ? undefined : storyTypeFilterValue.id,
      saved: savedStoriesFilterValue,
      tagIds: tagsFilterValue.length ? tagsFilterValue : []
    },
    onCompleted: data => {
      if (!areTagsListDownloaded || currentLab !== selectedLab) {
        const tagFiltersList = data.coordinator.storyTag.list.map(tag => tag.id)
        setTagsFilterValue(tagFiltersList)
        setDefaultTagsFilterValue(tagFiltersList)
        setAreTagsListDownloaded(true)
        setCurrentLab(selectedLab)
      }
    },
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    if (
      waveFilterValue.id !== ALL_USERS_FILTER.id ||
      storyTypeFilterValue.id !== ALL_STORY_TYPES_FILTER.id ||
      tagsFilterValue.length < defaultTagsFilterValue.length ||
      savedStoriesFilterValue
    ) {
      setIsSomeFilteringApplied(true)
    } else {
      setIsSomeFilteringApplied(false)
    }
  }, [waveFilterValue.id, storyTypeFilterValue.id, savedStoriesFilterValue, tagsFilterValue.length, defaultTagsFilterValue.length])

  useEffect(() => {
    if (data && data.coordinator.story.list.length) {
      const combinedLinks = {}
      const linksToSingArray = data.coordinator.story.list
        .filter(story => story.resourceType === VIDEO_TYPE && story.thumbnailLocation)
        .map(story => story.thumbnailLocation)

      ;(async () => {
        const { data: signedLinks } = await client.query({
          query: SIGN_FILE_LINKS,
          variables: { linksToSign: linksToSingArray },
          fetchPolicy: 'no-cache'
        })
        linksToSingArray.forEach((link, idx) => {
          combinedLinks[link] = signedLinks.me.security.signFileLinks[idx]
        })

        setLinksPairs(combinedLinks)
      })()
    }
  }, [data, client])

  const handleWaveFilterChange = value => {
    setWaveFilterValue(value)
  }

  const handleStoryTypeFilterChange = value => {
    setStoryTypeFilterValue(value)
  }

  // const handleSavedStoriesFilterChange = () => {
  //   setSavedStoriesFilterValue(!savedStoriesFilterValue)
  // }

  const handleCollapseTagBank = () => {
    setTagBankCollapsed(!isTagBankCollapsed)
  }

  const handleTagFilterChange = value => {
    if (tagsFilterValue.includes(value)) {
      setTagsFilterValue([...tagsFilterValue.filter(tagId => tagId !== value)])
    } else {
      setTagsFilterValue([...tagsFilterValue, value])
    }
  }

  // const handleGenerateReel = () => {
  //   console.log('generate reel')
  // }

  const handleClearFilters = () => {
    setWaveFilterValue(ALL_USERS_FILTER)
    setStoryTypeFilterValue(ALL_STORY_TYPES_FILTER)
    setTagsFilterValue([...defaultTagsFilterValue])
    setSavedStoriesFilterValue(false)
    setIsSomeFilteringApplied(false)
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={6} className={classes.wrapper}>
        <Grid container item xs={12}>
          <Typography variant='h2' component='div'>
            Story Review
          </Typography>
        </Grid>
        <Grid container item xs={12} className={classes.wrapper}>
          {loading || !selectedLab ? (
            <div className={classes.loader}>
              <CircularProgress color='inherit' />
            </div>
          ) : (
            <div className={classes.container}>
              <Grid container item xs={12} className={classes.filters}>
                <Select
                  MenuProps={MENU_PROPS}
                  value={waveFilterValue.id}
                  onChange={null}
                  input={<FilterInput />}
                  className={waveFilterValue.id === ALL_USERS_FILTER.id ? classes.dropdownSelect : classes.nonDefault}
                >
                  {[ALL_USERS_FILTER, ...sortBy(data.coordinator.wave.list, 'name'), NO_USERS_FILTER].map(item => {
                    return (
                      <MenuItem key={item.id} onClick={() => handleWaveFilterChange(item)} value={item.id} className={classes.menuItem}>
                        {item.name}
                      </MenuItem>
                    )
                  })}
                </Select>
                <Select
                  MenuProps={MENU_PROPS}
                  value={storyTypeFilterValue.id}
                  onChange={null}
                  input={<FilterInput />}
                  className={storyTypeFilterValue.id === ALL_STORY_TYPES_FILTER.id ? classes.dropdownSelect : classes.nonDefault}
                >
                  {STORY_TYPE_FILTERS.map(item => {
                    return (
                      <MenuItem
                        key={item.id}
                        onClick={() => handleStoryTypeFilterChange(item)}
                        value={item.id}
                        className={classes.menuItem}
                      >
                        {item.name}
                      </MenuItem>
                    )
                  })}
                </Select>
                {/* <FormControlLabel
                  checked={savedStoriesFilterValue}
                  onChange={handleSavedStoriesFilterChange}
                  control={<Checkbox color='default' />}
                  label={
                    <Typography variant='h5' component='div' className={classes.label}>
                      Saved Stories
                    </Typography>
                  }
                  labelPlacement='start'
                /> */}
                {isSomeFilteringApplied && (
                  <Button color='inherit' size='small' className={classes.clearFilters} endIcon={<Close />} onClick={handleClearFilters}>
                    CLEAR
                  </Button>
                )}
                {/* <Button
                  variant='contained'
                  color='default'
                  className={classes.generateReel}
                  startIcon={<Launch />}
                  onClick={handleGenerateReel}
                >
                  GENERATE REEL
                </Button> */}
              </Grid>
              <div className={classes.divider} />
              <Grid container item xs={12} className={classes.tagBank}>
                <Grid container item xs={12} className={classes.tagBankHeader}>
                  <Grid item xs={6}>
                    <Button
                      color='default'
                      className={classes.tagBankButton}
                      endIcon={isTagBankCollapsed ? <ArrowDropDown className={classes.arrow} /> : <ArrowDropUp className={classes.arrow} />}
                      onClick={handleCollapseTagBank}
                    >
                      <Typography variant='h6' component='div' style={{ color: !isTagBankCollapsed ? '#F7901E' : '#8688A1' }}>
                        TAG BANK
                      </Typography>
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant='h5' component='div' className={classes.totalStories}>
                      {`${data.coordinator.story.list.length} Total Stories`}{' '}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item xs={12} wrap='wrap'>
                  {!isTagBankCollapsed
                    ? sortBy(data.coordinator.storyTag.list, ['name']).map((tag, idx) => (
                        <Grid
                          item
                          xs={4}
                          key={tag.id}
                          className={(idx + 1) % 3 === 0 ? classes.lastColumn : idx % 3 === 0 ? classes.firstColumn : classes.middleColumn}
                        >
                          <CardMedia
                            component='img'
                            src={TAG_ICONS_MAP[tag.name] || Patient}
                            alt='icon'
                            style={{ width: 30, height: 30 }}
                          />
                          <Typography variant='h5' component='div' className={classes.tagName}>
                            {`${tag.name} (${tag.usageCount})`}
                          </Typography>
                          <Checkbox
                            color='default'
                            value={tag.id}
                            checked={tagsFilterValue.includes(tag.id)}
                            onChange={() => handleTagFilterChange(tag.id)}
                            className={classes.tagCheckbox}
                          />
                        </Grid>
                      ))
                    : null}
                </Grid>
              </Grid>
              <div className={classes.divider} style={isTagBankCollapsed ? { borderColor: 'transparent', margin: '8px 0' } : {}} />

              <Grid container item xs={12} spacing={2} wrap='wrap' justifyContent='center'>
                {data.coordinator.story.list.length ? (
                  data.coordinator.story.list.map(card => <StoryCard key={card.id} card={card} linksPairs={linksPairs} showMuteButton />)
                ) : (
                  <div className={classes.empty}>
                    <div className={classes.icon}>
                      <ErrorIcon />
                    </div>
                    <Typography variant='h5' component='div' className={classes.header}>
                      Your learners haven’t shared any stories yet!
                    </Typography>
                    <Typography variant='h5' component='div' className={classes.text}>
                      Your learners haven’t shared any stories yet! Give them a nudge with an note. Click here to go to ‘Management’ so you
                      can generate an email list.
                    </Typography>
                  </div>
                )}
              </Grid>
            </div>
          )}
        </Grid>
      </Grid>
    </div>
  )
}

export default StoryReview
